import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert
} from '@mui/material';
import { claimsAPI } from '../services/api';
import ClaimStatusBadge from '../components/Claims/ClaimStatusBadge';
import ClaimTimeline from '../components/Claims/ClaimTimeline';
import ClaimMessageThread from '../components/Claims/ClaimMessageThread';

function formatDate(d) {
  return d ? new Date(d).toLocaleDateString() : '—';
}
function formatDateTime(d) {
  return d ? new Date(d).toLocaleString() : '—';
}
function formatCurrency(n) {
  return n != null ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n) : '—';
}

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ py: 2 }}>{children}</Box> : null;
}

export default function ClaimDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [claim, setClaim] = useState(null);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const data = await claimsAPI.get(id);
        setClaim(data);
      } catch (e) {
        setError(e.response?.data?.error || e.message || 'Failed to load claim');
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) return <Container sx={{ py: 3 }}><CircularProgress /></Container>;
  if (error || !claim) return <Container sx={{ py: 3 }}><Alert severity="error">{error || 'Claim not found'}</Alert><Button onClick={() => navigate('/claims')}>Back to Claims</Button></Container>;

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight="bold">Claim {claim.claim_number}</Typography>
        <ClaimStatusBadge status={claim.status} />
      </Box>
      <Typography color="textSecondary">Policy: {claim.policy_type} ({claim.policy_number}) · {claim.insurer_name}</Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mt: 2 }}>
        <Tab label="Overview" />
        <Tab label="Documents" />
        <Tab label="Status history" />
        <Tab label="Messages" />
      </Tabs>

      <TabPanel value={tab} index={0}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle2" color="textSecondary">Service</Typography>
          <Typography>Date: {formatDate(claim.service_date)} · Provider: {claim.service_provider || '—'}</Typography>
          <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 1 }}>Financial</Typography>
          <Typography>Claimed: {formatCurrency(claim.claimed_amount)} · Approved: {formatCurrency(claim.approved_amount)}</Typography>
          <Typography>Deductible: {formatCurrency(claim.deductible_amount)} · Co-pay: {formatCurrency(claim.co_payment_amount)}</Typography>
          <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 1 }}>Submitted: {formatDateTime(claim.submission_date)}</Typography>
        </Paper>
      </TabPanel>

      <TabPanel value={tab} index={1}>
        {!claim.documents || claim.documents.length === 0 ? (
          <Typography color="textSecondary">No documents.</Typography>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Uploaded</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {claim.documents.map((doc) => (
                  <TableRow key={doc.id}>
                    <TableCell>{doc.document_type}</TableCell>
                    <TableCell>{doc.document_name}</TableCell>
                    <TableCell>{formatDate(doc.upload_date)}</TableCell>
                    <TableCell><Button size="small" href={doc.file_url} target="_blank" rel="noopener noreferrer">View</Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      <TabPanel value={tab} index={2}>
        <ClaimTimeline history={claim.status_history} />
      </TabPanel>

      <TabPanel value={tab} index={3}>
        <ClaimMessageThread claimId={claim.id} messages={claim.messages} />
      </TabPanel>

      <Button sx={{ mt: 2 }} onClick={() => navigate('/claims')}>Back to Claims</Button>
    </Container>
  );
}
